import {
  isAnnotationIdLocked,
  selectionHasLockedAnnotation,
} from './annotationLockUtils';
import { IMAGE_NODE_ID } from './constants';

const buildAnnotationsFromIds = (annotations, newIds) => {
  const currentIds = Object.keys(annotations);
  if (
    currentIds.length === newIds.length &&
    currentIds.every((id, index) => id === newIds[index])
  ) {
    return annotations;
  }

  return Object.fromEntries(newIds.map((key) => [key, annotations[key]]));
};

export const sortIdsByStackOrder = (stackIds, annotationIds) =>
  [...new Set(annotationIds)]
    .filter((id) => stackIds.includes(id))
    .sort((a, b) => stackIds.indexOf(a) - stackIds.indexOf(b));

const getSelectionIndices = (stackIds, selected) =>
  selected.map((id) => stackIds.indexOf(id));

const isContiguousSelection = (indices) => {
  if (!indices.length) {
    return false;
  }
  const sorted = [...indices].sort((a, b) => a - b);
  return sorted[sorted.length - 1] - sorted[0] + 1 === sorted.length;
};

/** One step up: swap the layer above the selection block with the block (contiguous). */
const moveContiguousSelectionForwardOneStep = (stackIds, selected) => {
  const indices = getSelectionIndices(stackIds, selected);
  const minIdx = Math.min(...indices);
  const maxIdx = Math.max(...indices);

  if (maxIdx >= stackIds.length - 1) {
    return stackIds;
  }

  const newIds = [...stackIds];
  const [neighborAbove] = newIds.splice(maxIdx + 1, 1);
  newIds.splice(minIdx, 0, neighborAbove);
  return newIds;
};

/** One step down: swap the layer below the selection block with the block (contiguous). */
const moveContiguousSelectionBackwardOneStep = (stackIds, selected) => {
  const indices = getSelectionIndices(stackIds, selected);
  const minIdx = Math.min(...indices);
  const maxIdx = Math.max(...indices);

  if (minIdx <= 0) {
    return stackIds;
  }

  const newIds = [...stackIds];
  const [neighborBelow] = newIds.splice(minIdx - 1, 1);
  newIds.splice(maxIdx, 0, neighborBelow);
  return newIds;
};

const reorderAnnotationsInStack = (annotations, id, direction) => {
  if (!annotations?.[id]) {
    return annotations;
  }

  const ids = Object.keys(annotations);
  const index = ids.indexOf(id);
  if (index === -1) {
    return annotations;
  }

  const newIds = [...ids];

  switch (direction) {
    case 'forward':
      if (index < newIds.length - 1) {
        [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
      }
      break;
    case 'backward':
      if (index > 0) {
        [newIds[index], newIds[index - 1]] = [newIds[index - 1], newIds[index]];
      }
      break;
    case 'front':
      if (index === newIds.length - 1) {
        return annotations;
      }
      newIds.splice(index, 1);
      newIds.push(id);
      break;
    case 'back':
      if (index === 0) {
        return annotations;
      }
      newIds.splice(index, 1);
      newIds.unshift(id);
      break;
    default:
      return annotations;
  }

  return buildAnnotationsFromIds(annotations, newIds);
};

/**
 * Whether the selection can move in this direction (stack: first key = back, last = front).
 */
export const canReorderSelection = (annotations, annotationIds, direction) => {
  if (!annotations || !annotationIds?.length || !direction) {
    return false;
  }

  if (selectionHasLockedAnnotation(annotations, annotationIds)) {
    return false;
  }

  const stackIds = Object.keys(annotations);
  const selected = sortIdsByStackOrder(stackIds, annotationIds);

  if (!selected.length) {
    return false;
  }

  const indices = getSelectionIndices(stackIds, selected);

  switch (direction) {
    case 'forward':
      return indices.some((i) => i < stackIds.length - 1);
    case 'backward':
      return indices.some((i) => i > 0);
    case 'front':
      return indices.some((i) => i < stackIds.length - 1);
    case 'back':
      return indices.some((i) => i > 0);
    default:
      return false;
  }
};

/**
 * Reorder one or more annotations; preserves relative order within the selection.
 */
export const reorderManyAnnotationsInStack = (annotations, annotationIds, direction) => {
  if (!annotations || !annotationIds?.length || !direction) {
    return annotations;
  }

  const stackIds = Object.keys(annotations);
  const selected = sortIdsByStackOrder(stackIds, annotationIds);

  if (!selected.length || !canReorderSelection(annotations, annotationIds, direction)) {
    return annotations;
  }

  if (direction === 'front' || direction === 'back') {
    const selectedSet = new Set(selected);
    const rest = stackIds.filter((id) => !selectedSet.has(id));
    const newIds =
      direction === 'front' ? [...rest, ...selected] : [...selected, ...rest];
    return buildAnnotationsFromIds(annotations, newIds);
  }

  if (
    selected.length > 1 &&
    isContiguousSelection(getSelectionIndices(stackIds, selected))
  ) {
    const newIds =
      direction === 'forward'
        ? moveContiguousSelectionForwardOneStep(stackIds, selected)
        : moveContiguousSelectionBackwardOneStep(stackIds, selected);
    return buildAnnotationsFromIds(annotations, newIds);
  }

  const processOrder =
    direction === 'forward' ? [...selected].reverse() : selected;

  return processOrder.reduce(
    (stack, annotationId) =>
      reorderAnnotationsInStack(stack, annotationId, direction),
    annotations,
  );
};

export const getAnnotationStackOrderKey = (annotations) =>
  Object.keys(annotations || {}).join('\0');

const LAYER_DIRECTIONS = ['forward', 'backward', 'front', 'back'];

/**
 * UI + action gating for layer order controls.
 * Disabled when fewer than two annotations or nothing selected.
 */
export const getLayerOrderAvailability = (annotations, annotationIds) => {
  const stackIds = Object.keys(annotations || {});
  const stackCount = stackIds.length;
  const hasSelection = Boolean(annotationIds?.length);
  const hasLockedSelection = selectionHasLockedAnnotation(
    annotations,
    annotationIds,
  );
  const enabled = stackCount >= 2 && hasSelection && !hasLockedSelection;

  const directions = LAYER_DIRECTIONS.reduce(
    (acc, direction) => ({
      ...acc,
      [direction]: enabled
        ? canReorderSelection(annotations, annotationIds, direction)
        : false,
    }),
    {},
  );

  return {
    enabled,
    stackCount,
    hasSelection,
    hasLockedSelection,
    directions,
  };
};

const ANNOTATION_Z_INDEX_BASE = 1;

/** Match `AnnotationNodes` — hidden layers are not mounted in Konva. */
export const getVisibleAnnotationStackIds = (annotations) =>
  Object.keys(annotations || {}).filter(
    (id) => annotations[id]?.visible !== false,
  );

/**
 * Align Konva sibling z-order with annotations key order (back → front).
 * Keeps background image at 0 and optional preview group above annotations.
 */
export const syncKonvaStackingOrder = (
  designLayer,
  annotations,
  previewNode = null,
) => {
  if (!designLayer || !annotations) {
    return;
  }

  const stackIds = getVisibleAnnotationStackIds(annotations);
  const assignments = [];

  const imageNode = designLayer.findOne(`#${IMAGE_NODE_ID}`);
  if (imageNode && typeof imageNode.zIndex === 'function') {
    assignments.push([imageNode, 0]);
  }

  let nextZIndex = ANNOTATION_Z_INDEX_BASE;
  stackIds.forEach((id) => {
    const node = designLayer.findOne(`#${id}`);
    if (!node) {
      return;
    }

    if (typeof node.zIndex === 'function') {
      assignments.push([node, nextZIndex]);
      nextZIndex += 1;
    } else {
      node.moveToTop();
    }
  });

  if (previewNode && typeof previewNode.zIndex === 'function') {
    assignments.push([previewNode, nextZIndex]);
  }

  // Highest index first — avoids transient out-of-range zIndex while reordering.
  assignments
    .sort((a, b) => b[1] - a[1])
    .forEach(([node, zIndex]) => {
      node.zIndex(zIndex);
    });

  if (typeof designLayer.batchDraw === 'function') {
    designLayer.batchDraw();
  }
};

export default reorderAnnotationsInStack;
